import IconArrowLeft from '@apollo/icons/default/IconArrowLeft.svg';
import IconArrowRight from '@apollo/icons/default/IconArrowRight.svg';
import IconChevronEndLeft from '@apollo/icons/default/IconChevronEndLeft.svg';
import IconChevronEndRight from '@apollo/icons/default/IconChevronEndRight.svg';
import IconChevronLeft from '@apollo/icons/default/IconChevronLeft.svg';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import { ButtonGroup, IconButton, Spinner, Text } from '@apollo/orbit';
import React from 'react';

import { useLDFlag } from 'src/lib/launchDarkly';

interface PaginationControlsTypes {
  /**
   * 0-based page number.
   */
  currentPage: number;
  totalCount: number;
  pageSize: number;
  loading?: boolean;
  onGoToFirstPageClick: () => void;
  onGoToLastPageClick: () => void;
  onPreviousPageClick: () => void;
  onNextPageClick: () => void;
}

export function startIndexForCurrentPage(
  currentPage: number,
  pageSize: number,
  totalCount: number,
) {
  return Math.min(pageSize * currentPage, totalCount - 1);
}

export function endIndexForCurrentPage(
  currentPage: number,
  pageSize: number,
  totalCount: number,
) {
  return Math.min(pageSize * (currentPage + 1) - 1, totalCount - 1);
}

export function totalPageCount(pageSize: number, totalCount: number) {
  return Math.ceil(totalCount / pageSize);
}

export const PaginationControls = ({
  currentPage,
  totalCount,
  pageSize,
  loading,
  onGoToFirstPageClick,
  onGoToLastPageClick,
  onPreviousPageClick,
  onNextPageClick,
}: PaginationControlsTypes) => {
  const shouldShowNewUI = useLDFlag('gov-pq-ux-sort-filter-delete');

  const numPages = totalPageCount(pageSize, totalCount);
  const recordsBoundsStartIndex = startIndexForCurrentPage(
    currentPage,
    pageSize,
    totalCount,
  );
  const recordsBoundsEndIndex = endIndexForCurrentPage(
    currentPage,
    pageSize,
    totalCount,
  );

  if (shouldShowNewUI) {
    return (
      <>
        <ButtonGroup>
          <IconButton
            aria-label="Go to first page"
            variant="hidden"
            className="mr-3"
            isDisabled={currentPage === 0 || loading}
            onClick={onGoToFirstPageClick}
            data-testid="btn-first-page"
          >
            <IconChevronEndLeft />
          </IconButton>
          <IconButton
            aria-label="Previous page"
            variant="hidden"
            className="mr-3"
            isDisabled={currentPage === 0 || loading}
            onClick={onPreviousPageClick}
            data-testid="btn-prev-page"
          >
            <IconChevronLeft />
          </IconButton>
        </ButtonGroup>
        <div>
          <Text variant="body" data-testid="pagination-display">
            {loading ? (
              <Spinner size="tiny" className="mr-1" />
            ) : (
              <Text as="span" variant="bodySb">
                {recordsBoundsStartIndex + 1} - {recordsBoundsEndIndex + 1}
              </Text>
            )}{' '}
            of {totalCount}
          </Text>
        </div>
        <ButtonGroup>
          <IconButton
            aria-label="Next page"
            variant="hidden"
            className="mr-2"
            isDisabled={currentPage === numPages - 1 || loading}
            onClick={onNextPageClick}
            data-testid="btn-next-page"
          >
            <IconChevronRight />
          </IconButton>
          <IconButton
            aria-label="Go to last page"
            variant="hidden"
            className="mr-3"
            isDisabled={currentPage === numPages - 1 || loading}
            onClick={onGoToLastPageClick}
            data-testid="btn-last-page"
          >
            <IconChevronEndRight />
          </IconButton>
        </ButtonGroup>
      </>
    );
  }

  return (
    <section className="text-secondary">
      <span className="mr-3" data-testid="pagination-display">
        {loading ? (
          <Spinner size="tiny" />
        ) : (
          <>
            {recordsBoundsStartIndex + 1} - {recordsBoundsEndIndex + 1} of{' '}
            {totalCount}
          </>
        )}
      </span>
      <IconButton
        aria-label="Previous page"
        variant="secondary"
        className="mr-3"
        isDisabled={currentPage === 0 || loading}
        onClick={onPreviousPageClick}
        data-testid="btn-prev-page"
      >
        <IconArrowLeft />
      </IconButton>
      <IconButton
        aria-label="Next page"
        variant="secondary"
        className="mr-2"
        isDisabled={currentPage === numPages - 1 || loading}
        onClick={onNextPageClick}
        data-testid="btn-next-page"
      >
        <IconArrowRight />
      </IconButton>
    </section>
  );
};
